<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import {
  fetchSafetyInsightsPdf,
  listObservations,
  type ObservationRow,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["observations", selectedProjectId, "all"],
  queryFn: () => listObservations(selectedProjectId.value),
});

const closeCalls = computed(() =>
  (data.value ?? []).filter((o) => o.kind === "close_call"),
);
const goodPractice = computed(() =>
  (data.value ?? []).filter((o) => o.kind === "good_practice"),
);
const openCalls = computed(() =>
  closeCalls.value.filter((o) => o.status !== "closed"),
);
const closedPct = computed(() => {
  const n = closeCalls.value.length;
  if (!n) return null;
  return Math.round(((n - openCalls.value.length) / n) * 100);
});

interface Tally { label: string; count: number }
function tally(rows: ObservationRow[], key: (o: ObservationRow) => string): Tally[] {
  const m = new Map<string, number>();
  for (const o of rows) {
    const k = key(o) || "Unspecified";
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

const byType = computed(() => tally(closeCalls.value, (o) => o.observation_type ?? ""));
const byLocation = computed(() => tally(closeCalls.value, (o) => o.location_ref ?? ""));
function barPct(items: Tally[], count: number): number {
  const max = Math.max(1, ...items.map((i) => i.count));
  return Math.round((count / max) * 100);
}

// Week commencing (Monday) key — simple, unambiguous trend bucket.
function weekStart(iso: string): string {
  const d = new Date(iso);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}
const trend = computed(() => {
  const m = new Map<string, { raised: number; closed: number }>();
  const bump = (wk: string, k: "raised" | "closed") => {
    const e = m.get(wk) ?? { raised: 0, closed: 0 };
    e[k] += 1;
    m.set(wk, e);
  };
  for (const o of closeCalls.value) {
    bump(weekStart(o.raised_at), "raised");
    if (o.closed_at) bump(weekStart(o.closed_at), "closed");
  }
  return [...m.entries()]
    .map(([week, v]) => ({ week, ...v }))
    .sort((a, b) => a.week.localeCompare(b.week))
    .slice(-12);
});
const trendMax = computed(() => Math.max(1, ...trend.value.map((t) => t.raised)));

// "We Said, We Did" — closed close calls with the action taken.
const weSaidWeDid = computed(() =>
  closeCalls.value
    .filter((o) => o.status === "closed" && (o.action_taken ?? "").trim())
    .sort((a, b) => (b.closed_at ?? "").localeCompare(a.closed_at ?? ""))
    .slice(0, 15),
);

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  }) : "—";

// Branded, server-rendered PDF (ECS header, fixed layout).
const pdfBusy = ref(false);
const pdfError = ref<string | null>(null);
async function exportPdf(): Promise<void> {
  if (!selectedProjectId.value) return;
  pdfBusy.value = true;
  pdfError.value = null;
  try {
    const blob = await fetchSafetyInsightsPdf(selectedProjectId.value);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    pdfError.value = e instanceof Error ? e.message : String(e);
  } finally {
    pdfBusy.value = false;
  }
}
</script>

<template>
  <PageLayout wide>
    <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="mb-1 text-xl font-semibold">
          Safety insights
        </h1>
        <p class="text-sm text-neutral-500">
          Close-call trends and the "We Said, We Did" board — the evidence to
          feed toolbox talks and close the feedback loop.
        </p>
      </div>
      <div class="flex shrink-0 items-center gap-2 print:hidden">
        <span
          v-if="pdfError"
          class="text-xs text-semantic-danger"
        >{{ pdfError }}</span>
        <button
          type="button"
          class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
          title="Download a branded PDF of this safety insights report"
          :disabled="pdfBusy"
          @click="exportPdf"
        >
          {{ pdfBusy ? "Preparing…" : "Download PDF" }}
        </button>
      </div>
    </div>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="rounded-xl bg-neutral-0 p-8 text-center text-neutral-500 shadow"
    >
      No close calls or good practice logged on this project yet.
    </div>
    <div
      v-else-if="data"
      class="grid gap-6"
    >
      <!-- KPIs -->
      <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Close calls
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ closeCalls.length }}
          </p>
        </div>
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Open
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ openCalls.length }}
          </p>
        </div>
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Closed
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ closedPct === null ? "—" : closedPct + "%" }}
          </p>
          <p class="text-xs text-neutral-500">
            loop closed
          </p>
        </div>
        <div class="rounded-xl bg-neutral-0 p-4 shadow">
          <p class="text-xs uppercase tracking-wide text-neutral-400">
            Good practice
          </p>
          <p class="mt-1 text-2xl font-bold">
            {{ goodPractice.length }}
          </p>
        </div>
      </div>

      <!-- We Said, We Did -->
      <section class="rounded-xl bg-neutral-0 p-6 shadow">
        <h2 class="mb-1 font-semibold">
          We Said, We Did
        </h2>
        <p class="mb-4 text-xs text-neutral-400">
          Closed close calls and the action taken — share these in toolbox talks.
        </p>
        <p
          v-if="weSaidWeDid.length === 0"
          class="text-sm text-neutral-400"
        >
          No closed close calls with a recorded action yet.
        </p>
        <ul
          v-else
          class="grid gap-3"
        >
          <li
            v-for="o in weSaidWeDid"
            :key="o.id"
            class="grid gap-1 border-b border-neutral-50 pb-3 last:border-0"
          >
            <p class="text-sm">
              <span class="font-medium text-neutral-500">We said:</span>
              {{ o.description }}
            </p>
            <p class="text-sm">
              <span class="font-medium text-semantic-success">We did:</span>
              {{ o.action_taken }}
            </p>
            <p class="text-xs text-neutral-400">
              {{ o.location_ref ? o.location_ref + " · " : "" }}closed {{ fmtDate(o.closed_at) }}
              <span v-if="o.closed_by_name"> · {{ o.closed_by_name }}</span>
            </p>
          </li>
        </ul>
      </section>

      <div class="grid gap-6 lg:grid-cols-2">
        <!-- By type -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow">
          <h2 class="mb-1 font-semibold">
            Close calls by type
          </h2>
          <p class="mb-4 text-xs text-neutral-400">
            Where the near-misses cluster — the upstream hazards to fix.
          </p>
          <p
            v-if="!byType.length"
            class="text-sm text-neutral-400"
          >
            No close calls recorded.
          </p>
          <ul
            v-else
            class="grid gap-2"
          >
            <li
              v-for="item in byType"
              :key="item.label"
            >
              <div class="flex justify-between text-sm">
                <span class="truncate pr-2 capitalize text-neutral-700">{{ item.label }}</span>
                <span class="font-medium text-neutral-500">{{ item.count }}</span>
              </div>
              <div class="mt-1 h-2 rounded-full bg-neutral-100">
                <div
                  class="h-2 rounded-full bg-semantic-warning"
                  :style="{ width: barPct(byType, item.count) + '%' }"
                />
              </div>
            </li>
          </ul>
        </section>

        <!-- By location -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow">
          <h2 class="mb-1 font-semibold">
            Close calls by location
          </h2>
          <p class="mb-4 text-xs text-neutral-400">
            Hotspots across the project.
          </p>
          <p
            v-if="!byLocation.length"
            class="text-sm text-neutral-400"
          >
            No close calls recorded.
          </p>
          <ul
            v-else
            class="grid gap-2"
          >
            <li
              v-for="item in byLocation"
              :key="item.label"
            >
              <div class="flex justify-between text-sm">
                <span class="truncate pr-2 text-neutral-700">{{ item.label }}</span>
                <span class="font-medium text-neutral-500">{{ item.count }}</span>
              </div>
              <div class="mt-1 h-2 rounded-full bg-neutral-100">
                <div
                  class="h-2 rounded-full bg-brand-primary"
                  :style="{ width: barPct(byLocation, item.count) + '%' }"
                />
              </div>
            </li>
          </ul>
        </section>

        <!-- Trend -->
        <section class="rounded-xl bg-neutral-0 p-6 shadow lg:col-span-2">
          <h2 class="mb-1 font-semibold">
            Trend by week
          </h2>
          <p class="mb-4 text-xs text-neutral-400">
            Close calls raised (amber) vs closed (green), week commencing.
          </p>
          <p
            v-if="!trend.length"
            class="text-sm text-neutral-400"
          >
            No close calls yet.
          </p>
          <div
            v-else
            class="flex items-end gap-2"
            style="height: 140px"
          >
            <div
              v-for="t in trend"
              :key="t.week"
              class="flex flex-1 flex-col items-center justify-end gap-1"
              :title="`w/c ${t.week}: ${t.raised} raised, ${t.closed} closed`"
            >
              <div class="flex w-full items-end justify-center gap-0.5">
                <div
                  class="w-2.5 rounded-t bg-semantic-warning"
                  :style="{ height: (t.raised / trendMax) * 110 + 'px' }"
                />
                <div
                  class="w-2.5 rounded-t bg-semantic-success"
                  :style="{ height: (t.closed / trendMax) * 110 + 'px' }"
                />
              </div>
              <span class="text-[10px] text-neutral-400">{{ t.week.slice(5) }}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  </PageLayout>
</template>
